import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const flagVariants = cva('inline-block overflow-hidden bg-muted', {
  variants: {
    size: {
      xs: 'w-4 h-3', // 16x12
      sm: 'w-5 h-[13px]', // 20x15
      md: 'w-6 h-[18px]', // 24x18
      lg: 'w-8 h-6', // 32x24
      xl: 'w-12 h-9', // 48x36
    },
    rounded: {
      none: 'rounded-none',
      sm: 'rounded-sm',
      md: 'rounded',
      lg: 'rounded-md',
      full: 'rounded-full',
    },
    // Grayscale/opacity treatment for status states. Pass alongside the parent
    // container's ring color to communicate live/completed/upcoming.
    status: {
      none: '',
      live: '',
      completed: 'opacity-60 grayscale',
      upcoming: '',
    },
  },
  defaultVariants: {
    size: 'md',
    rounded: 'sm',
    status: 'none',
  },
});

export interface FlagProps
  extends React.ImgHTMLAttributes<HTMLImageElement>, VariantProps<typeof flagVariants> {
  /**
   * ISO 3166-1 alpha-2 country code (e.g., "US", "AU", "GB")
   */
  code: string;
  /**
   * Whether to show a subtle border around the flag
   */
  hasBorder?: boolean;
  /**
   * Whether to show a drop shadow
   */
  hasShadow?: boolean;
}

/**
 * Flag component that displays country flags using ISO 3166-1 alpha-2 codes.
 *
 * @example
 * ```tsx
 * <Flag code="AU" size="md" />
 * <Flag code="US" size="lg" hasBorder />
 * <Flag code="GB" size="sm" rounded="md" hasShadow />
 * <Flag code="JP" rounded="full" status="completed" className="h-full w-full object-cover" />
 * ```
 */
const Flag = React.forwardRef<HTMLImageElement, FlagProps>(
  (
    { className, code, size, rounded, status, hasBorder = false, hasShadow = false, alt, ...props },
    ref,
  ) => {
    if (!code) return null;
    const upperCode = code.toUpperCase();
    const src = `/flags/${upperCode}.svg`;

    // Generate unicode flag emoji as fallback alt text
    const unicodeFlag = upperCode
      .split('')
      .map((char) => String.fromCodePoint(0x1f1e6 + (char.charCodeAt(0) - 65)))
      .join('');

    return (
      <img
        ref={ref}
        src={src}
        alt={alt ?? `${upperCode} flag ${unicodeFlag}`}
        className={cn(
          flagVariants({ size, rounded, status }),
          hasBorder && 'ring-1 ring-border/50',
          hasShadow && 'shadow-sm',
          className,
        )}
        {...props}
      />
    );
  },
);
Flag.displayName = 'Flag';

export { Flag, flagVariants };
